import React from 'react';
import { StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import JoinGroupButton from '@components/Group/JoinButton';
import Icon from '@components/Icon';
import { PressableOpacity } from '@components/Pressable';
import Surface from '@components/Surface';
import { Caption, Paragraph } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { Group } from '@types';

const LARGE_AVATAR_SIZE = 50;
const SMALL_AVATAR_SIZE = 50;
const ICON_SIZE = 20;

export type GroupData = Pick<
  Group,
  'displayName' | 'description' | 'numMembers' | 'photoUrl' | 'verified' | 'groupId'
>;

export type GroupCardProps = {
  data: GroupData;
  caption?: {
    captionIcon: React.ComponentProps<typeof Icon>['name'];
    captionText: string;
  };
  onPress: (groupId: string) => void;
  showJoinButton?: boolean;
} & Omit<React.ComponentProps<typeof Surface>, 'children'>;

export function LargeGroupCard({
  data,
  caption,
  onPress,
  showJoinButton = true,
  style,
  ...props
}: GroupCardProps) {
  const appTheme = useAppTheme();

  return (
    <PressableOpacity
      onPress={() => onPress(data.groupId)}
      style={[largeStyles.pressable, { maxWidth: appTheme.maxWidth }]}
      testID={data.groupId}>
      <Surface
        style={[
          styles.container,
          largeStyles.container,
          { maxWidth: appTheme.maxWidth },
          style,
          { elevation: undefined },
        ]}
        {...props}>
        <View style={[styles.row, largeStyles.full]}>
          <UserAvatar
            displayName={data.displayName}
            photoUrl={data.photoUrl}
            size={LARGE_AVATAR_SIZE}
            style={styles.avatar}
          />
          <View style={[largeStyles.column, largeStyles.expanding]}>
            <Paragraph style={styles.text} numberOfLines={1}>
              {data.displayName}
            </Paragraph>
            <View style={styles.row}>
              <Icon
                name={caption?.captionIcon || 'account-multiple-outline'}
                size={ICON_SIZE}
                style={largeStyles.icon}
              />
              <Caption style={styles.text} numberOfLines={1}>
                {caption?.captionText || (data.numMembers || 0) + ' members'}
              </Caption>
              {data.verified ? (
                <>
                  <Icon name="check-circle" size={ICON_SIZE} style={largeStyles.icon} />
                  <Caption style={styles.text} numberOfLines={1}>
                    Verified
                  </Caption>
                </>
              ) : null}
            </View>
          </View>
          {showJoinButton ? (
            <JoinGroupButton
              variant="small"
              groupId={data.groupId}
              labelStyle={largeStyles.joinButtonText}
              style={largeStyles.joinButton}
            />
          ) : null}
        </View>
      </Surface>
    </PressableOpacity>
  );
}

export function SmallGroupCard({
  data,
  caption,
  onPress,
  showJoinButton = true,
  style,
  ...props
}: GroupCardProps) {
  return (
    <PressableOpacity onPress={() => onPress(data.groupId)} testID={data.groupId}>
      <Surface style={[styles.container, smallStyles.container, style]} {...props}>
        <View style={styles.title}>
          <UserAvatar
            displayName={data.displayName}
            photoUrl={data.photoUrl}
            size={SMALL_AVATAR_SIZE}
            style={smallStyles.avatar}
          />
        </View>
        <Paragraph style={[styles.displayName, styles.text]} numberOfLines={1}>
          {data.displayName}
        </Paragraph>
        <View style={[styles.row, smallStyles.captionContainer]}>
          <Icon name={caption?.captionIcon || 'account-multiple-outline'} size={ICON_SIZE} />
          <Caption style={[smallStyles.caption, styles.text]} numberOfLines={1}>
            {caption?.captionText || (data.numMembers || 0) + ' members'}
          </Caption>
        </View>
        {showJoinButton ? (
          <JoinGroupButton
            variant="small"
            groupId={data.groupId}
            labelStyle={smallStyles.buttonLabel}
            style={smallStyles.button}
          />
        ) : null}
      </Surface>
    </PressableOpacity>
  );
}

const styles = StyleSheet.create({
  avatar: {
    margin: 5,
  },
  container: {
    margin: 5,
  },
  displayName: {
    alignSelf: 'center',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  text: {
    flexShrink: 1,
    marginRight: 5,
  },
  title: {
    alignSelf: 'center',
    maxWidth: '100%',
  },
});

const largeStyles = StyleSheet.create({
  column: {
    flex: 1,
    minWidth: 0,
    padding: 5,
  },
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    minWidth: 250,
    padding: 5,
  },
  expanding: {
    flex: 1,
    flexGrow: 1,
  },
  full: {
    width: '100%',
  },
  icon: {
    marginRight: 5,
  },
  joinButton: {
    flexShrink: 0,
    margin: 5,
    width: 100,
  },
  joinButtonText: {
    fontSize: 12,
  },
  pressable: {
    alignSelf: 'center',
    flexShrink: 0,
    width: '100%',
  },
});

const smallStyles = StyleSheet.create({
  avatar: {
    marginBottom: 5,
  },
  button: {
    marginVertical: 5,
    width: '100%',
  },
  buttonLabel: {
    fontWeight: 'normal',
  },
  caption: {
    marginLeft: 5,
  },
  captionContainer: {
    justifyContent: 'center',
  },
  container: {
    padding: 10,
    width: 125,
  },
});
